import { useState } from "react";
import { api, errText, mfaTrust } from "../lib/api";
import { ShieldCheck, KeyRound, Loader2, Copy, Check } from "lucide-react";

const METHOD_LABEL = {
  totp: "Authenticator app (Google Authenticator, Authy)",
  email: "Email code",
  sms: "SMS code",
};

// ---------- Step during login: verify the second factor ----------
export const MfaChallenge = ({ challenge, method, hint, onVerified, onCancel }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recovery, setRecovery] = useState(false);
  const [sent, setSent] = useState("");

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const value = recovery ? `recovery:${code.trim()}` : code.trim();
      const { data } = await api.post("/auth/mfa/verify", { challenge_token: challenge, code: value });
      onVerified(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(""); setSent("");
    try {
      const { data } = await api.post("/auth/mfa/challenge/send", { challenge_token: challenge });
      setSent(`A new code was sent to ${data.destination_hint || "you"}.`);
    } catch (err) {
      setError(errText(err));
    }
  };

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="mfa-challenge">
      <div className="flex items-center gap-2 text-ink">
        <ShieldCheck size={20} className="text-brass" />
        <h2 className="font-display text-xl">Two-step verification</h2>
      </div>
      <p className="text-sm text-muted" data-testid="mfa-challenge-method">
        {method === "totp"
          ? "Enter the 6-digit code from your authenticator app."
          : `Enter the code we sent to ${hint || "you"}.`}
      </p>
      <input data-testid="mfa-code-input" className="field tracking-[0.4em] text-center" inputMode="numeric"
             autoFocus placeholder={recovery ? "recovery code" : "••••••"} value={code}
             onChange={(e) => setCode(e.target.value)} />
      {error ? <p className="text-danger text-sm" data-testid="mfa-error">{error}</p> : null}
      {sent ? <p className="text-success text-sm" data-testid="mfa-sent">{sent}</p> : null}
      <button className="btn-primary w-full" disabled={busy} data-testid="mfa-verify-button">
        {busy ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Verify & continue"}
      </button>
      <div className="flex items-center justify-between text-xs">
        {method !== "totp" ? (
          <button type="button" className="text-brass underline" onClick={resend} data-testid="mfa-resend">Resend code</button>
        ) : <span />}
        <button type="button" className="text-muted underline" onClick={() => { setRecovery(!recovery); setCode(""); }}
                data-testid="mfa-use-recovery">
          {recovery ? "Use my normal code" : "Use a recovery code"}
        </button>
      </div>
      {onCancel ? (
        <button type="button" className="text-xs text-muted underline w-full" onClick={onCancel} data-testid="mfa-cancel">
          Back to sign in
        </button>
      ) : null}
    </form>
  );
};

// ---------- Enrolment: pick a method, scan/verify, save recovery codes ----------
export const MfaEnroll = ({ allowedMethods = ["totp"], forced = false, onDone, onCancel }) => {
  const [method, setMethod] = useState(allowedMethods[0] || "totp");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [copied, setCopied] = useState(false);

  const begin = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/enroll/start", { method });
      setSetup(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm", { code: code.trim() });
      mfaTrust.save(data);
      if (data?.token) {
        const wasAdmin = localStorage.getItem("fl_admin_token") === localStorage.getItem("fl_token");
        localStorage.setItem("fl_token", data.token);
        if (wasAdmin) localStorage.setItem("fl_admin_token", data.token);
      }
      setRecovery(data.recovery_codes);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = () => {
    navigator.clipboard?.writeText(recovery.join("\n"));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  if (recovery) {
    return (
      <div className="space-y-4" data-testid="mfa-recovery">
        <div className="flex items-center gap-2 text-ink"><KeyRound size={20} className="text-brass" />
          <h2 className="font-display text-xl">Save your recovery codes</h2></div>
        <p className="text-sm text-muted">Keep these somewhere safe. Each one lets you sign in once if you lose your device.</p>
        <div className="grid grid-cols-2 gap-2 bg-plaster-deep p-4 font-mono text-sm text-ink" data-testid="recovery-codes">
          {recovery.map((c) => <span key={c}>{c}</span>)}
        </div>
        <button type="button" className="btn-secondary w-full flex items-center justify-center gap-2" onClick={copyCodes} data-testid="copy-recovery">
          {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy codes</>}
        </button>
        <button className="btn-primary w-full" onClick={onDone} data-testid="mfa-done">I've saved them — finish</button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mfa-enroll">
      <div className="flex items-center gap-2 text-ink"><ShieldCheck size={20} className="text-brass" />
        <h2 className="font-display text-xl">Set up two-step verification</h2></div>
      {forced ? <p className="text-sm text-petal" data-testid="mfa-forced-note">Two-step verification is required for your account. Please set it up to continue.</p> : null}

      {!setup ? (
        <>
          <div className="space-y-2">
            {allowedMethods.map((m) => (
              <label key={m} className={`flex items-start gap-2 p-3 border cursor-pointer ${method === m ? "border-brass bg-plaster-deep" : "border-brass-light"}`}
                     data-testid={`mfa-method-${m}`}>
                <input type="radio" name="mfa-method" checked={method === m} onChange={() => setMethod(m)} className="mt-1" />
                <span className="text-sm text-ink">{METHOD_LABEL[m]}</span>
              </label>
            ))}
          </div>
          {error ? <p className="text-danger text-sm" data-testid="mfa-error">{error}</p> : null}
          <button className="btn-primary w-full" onClick={begin} disabled={busy} data-testid="mfa-begin">
            {busy ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Begin setup"}
          </button>
          {onCancel && !forced ? (
            <button type="button" className="text-xs text-muted underline w-full" onClick={onCancel}>Cancel</button>
          ) : null}
        </>
      ) : (
        <form onSubmit={confirm} className="space-y-3">
          {setup.method === "totp" ? (
            <div className="text-center space-y-2">
              <img src={setup.qr_data_url} alt="Scan with your authenticator app" className="mx-auto w-44 h-44 border border-brass-light" data-testid="mfa-qr" />
              <p className="text-xs text-muted">Can't scan? Enter this key manually:</p>
              <code className="block text-sm text-ink break-all bg-plaster-deep p-2" data-testid="mfa-secret">{setup.secret}</code>
            </div>
          ) : (
            <p className="text-sm text-muted" data-testid="mfa-code-sent">We sent a code to {setup.destination_hint}. Enter it below.</p>
          )}
          <input data-testid="mfa-confirm-input" className="field tracking-[0.4em] text-center" inputMode="numeric"
                 autoFocus placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value)} />
          {error ? <p className="text-danger text-sm" data-testid="mfa-error">{error}</p> : null}
          <button className="btn-primary w-full" disabled={busy} data-testid="mfa-confirm">
            {busy ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Verify & enable"}
          </button>
          <button type="button" className="text-xs text-muted underline w-full" onClick={() => { setSetup(null); setCode(""); }}>
            Choose a different method
          </button>
        </form>
      )}
    </div>
  );
};

// ---------- Settings panel: change password + manage 2FA ----------
const STR = {
  en: {
    password: "Password", emailNote: "Your email can't be changed here.",
    current: "Current password", newpw: "New password (min 8 characters)", repeat: "Repeat new password",
    update: "Update password", updated: "Password updated.", mismatch: "The two new passwords don't match.",
    twoStep: "Two-step verification", on: "On", left: "recovery codes left",
    requiredLock: "Required for your account — can't be turned off.", turnOff: "Turn off",
    confirmCode: "Enter a current code to confirm", confirmOff: "Confirm off", cancel: "Cancel",
    off: "Off. ", required: "Required for your account. ", addStep: "Add a second step so only you can sign in.",
    setup: "Set up now",
  },
  fr: {
    password: "Mot de passe", emailNote: "Votre e-mail ne peut pas être modifié ici.",
    current: "Mot de passe actuel", newpw: "Nouveau mot de passe (8 caractères min.)", repeat: "Répéter le nouveau mot de passe",
    update: "Modifier le mot de passe", updated: "Mot de passe mis à jour.", mismatch: "Les deux nouveaux mots de passe ne correspondent pas.",
    twoStep: "Vérification en deux étapes", on: "Activée", left: "codes de secours restants",
    requiredLock: "Obligatoire pour votre compte — ne peut pas être désactivée.", turnOff: "Désactiver",
    confirmCode: "Saisissez un code actuel pour confirmer", confirmOff: "Confirmer la désactivation", cancel: "Annuler",
    off: "Désactivée. ", required: "Obligatoire pour votre compte. ", addStep: "Ajoutez une seconde étape pour que vous seul puissiez vous connecter.",
    setup: "Activer maintenant",
  },
};

export const SecurityPanel = ({ status, onStatusChange, lang = "en" }) => {
  const s = STR[lang] || STR.en;
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState({ ok: "", error: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableOpen, setDisableOpen] = useState(false);
  const [mfaErr, setMfaErr] = useState("");

  const changePw = async (e) => {
    e.preventDefault();
    setPwMsg({ ok: "", error: "" });
    if (pw.new_password !== pw.confirm) { setPwMsg({ ok: "", error: s.mismatch }); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      if (data?.token) {
        const wasAdmin = localStorage.getItem("fl_admin_token") === localStorage.getItem("fl_token");
        localStorage.setItem("fl_token", data.token);
        if (wasAdmin) localStorage.setItem("fl_admin_token", data.token);
      }
      setPwMsg({ ok: s.updated, error: "" });
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwMsg({ ok: "", error: errText(err) });
    } finally {
      setPwBusy(false);
    }
  };

  const disable = async () => {
    setMfaErr("");
    try {
      await api.post("/auth/mfa/disable", { code: disableCode.trim() });
      setDisableOpen(false); setDisableCode("");
      onStatusChange?.();
    } catch (err) {
      setMfaErr(errText(err));
    }
  };

  return (
    <div className="space-y-8 max-w-lg" data-testid="security-panel">
      {/* Password */}
      <section className="bg-white border border-brass-light p-6 space-y-4">
        <h3 className="font-display text-lg text-ink flex items-center gap-2"><KeyRound size={18} className="text-brass" /> {s.password}</h3>
        <p className="text-xs text-muted">{s.emailNote}</p>
        <form onSubmit={changePw} className="space-y-3">
          <input data-testid="pw-current" type="password" className="field" placeholder={s.current}
                 value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          <input data-testid="pw-new" type="password" className="field" placeholder={s.newpw}
                 value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <input data-testid="pw-confirm" type="password" className="field" placeholder={s.repeat}
                 value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          {pwMsg.error ? <p className="text-danger text-sm" data-testid="pw-error">{pwMsg.error}</p> : null}
          {pwMsg.ok ? <p className="text-success text-sm" data-testid="pw-ok">{pwMsg.ok}</p> : null}
          <button className="btn-primary" disabled={pwBusy} data-testid="pw-submit">
            {pwBusy ? <Loader2 className="animate-spin" size={16} /> : s.update}
          </button>
        </form>
      </section>

      {/* 2FA */}
      <section className="bg-white border border-brass-light p-6 space-y-4">
        <h3 className="font-display text-lg text-ink flex items-center gap-2"><ShieldCheck size={18} className="text-brass" /> {s.twoStep}</h3>
        {status?.enabled ? (
          <div className="space-y-3" data-testid="mfa-enabled-state">
            <p className="text-sm text-success">{s.on} · {METHOD_LABEL[status.method] || status.method}</p>
            <p className="text-xs text-muted">{status.recovery_codes_left} {s.left}.</p>
            {status.required ? (
              <p className="text-xs text-muted" data-testid="mfa-required-lock">{s.requiredLock}</p>
            ) : !disableOpen ? (
              <button className="btn-secondary" onClick={() => setDisableOpen(true)} data-testid="mfa-disable-open">{s.turnOff}</button>
            ) : (
              <div className="space-y-2">
                <input className="field" placeholder={s.confirmCode} value={disableCode}
                       onChange={(e) => setDisableCode(e.target.value)} data-testid="mfa-disable-code" />
                {mfaErr ? <p className="text-danger text-sm">{mfaErr}</p> : null}
                <div className="flex gap-2">
                  <button className="btn-primary" onClick={disable} data-testid="mfa-disable-confirm">{s.confirmOff}</button>
                  <button className="btn-secondary" onClick={() => setDisableOpen(false)}>{s.cancel}</button>
                </div>
              </div>
            )}
          </div>
        ) : enrolling ? (
          <MfaEnroll allowedMethods={status?.methods_allowed || ["totp"]}
                     onDone={() => { setEnrolling(false); onStatusChange?.(); }}
                     onCancel={() => setEnrolling(false)} />
        ) : (
          <div className="space-y-3" data-testid="mfa-disabled-state">
            <p className="text-sm text-muted">
              {status?.required ? s.required : s.off}
              {s.addStep}
            </p>
            <button className="btn-primary" onClick={() => setEnrolling(true)} data-testid="mfa-enable">{s.setup}</button>
          </div>
        )}
      </section>
    </div>
  );
};
